// ════════════════════════════════════════════════════════════════
// ACCESS Subtrack + ICD-10 Resolver
// ════════════════════════════════════════════════════════════════
// Deterministic function that maps a patient's selected conditions to
// the ACCESS Track (eCKM/CKM), a Sub-Track ID (ECKM-01…CKM-10), and two
// ICD-10 views:
//   • cms_icd10        → CMS $align (combination codes + every OTHER reported
//                        condition; nothing dropped — clinical v2 2026-06-19)
//   • condition_icd10  → Elation problem list + chart (full per-condition)
//
// Source of truth = the clinical mapping PDF "Eligible conditions and
// outcomes" v2 (2026-06-19, CKM IDs renumbered to unique CKM-01…CKM-10).
//
// This module is the authoritative resolver. WF1 (n8n onboarding workflow)
// inlines a copy of this logic in its "Resolve ACCESS Subtrack" code node —
// keep both in sync. Static check S-16 fails the build on drift. If they
// diverge, the server copy in WF1 wins because it's the one that writes.
//
// Unit tests: scripts/test-access-subtrack-resolver.mjs
//
// FILE LAYOUT — two marker-bounded regions:
//   SUBTRACK-CORE  = the resolver WF1 inlines verbatim (S-16 sha-compares this
//                    region against WF1's "Resolve ACCESS Subtrack" node; a core
//                    edit REQUIRES regenerating the WF1 inline copy).
//   ICD-EXT        = extension consumed by WF15/WF12/the app via the
//                    sync-icd10-maps generator + direct import — NEVER inlined
//                    in WF1; editing it never touches onboarding.
// ════════════════════════════════════════════════════════════════

/* SUBTRACK-CORE-START */
// ─── Canonical per-condition ICD-10 + SNOMED (PDF-aligned) ────────
// Feeds condition_icd10 (Elation + chart) and the per-condition "extras"
// added to cms_icd10. SNOMED carried for Elation problems (unchanged from
// WF16's existing values). Used as WF12/WF24's rewritten fallback table.
const CONDITION_ICD10 = {
  htn:          { code: 'I10',    display: 'Essential (primary) hypertension',                snomed: '38341003' },
  dyslipidemia: { code: 'E78.5',  display: 'Hyperlipidemia, unspecified',                     snomed: '55822004' },
  overweight:   { code: 'E66.3',  display: 'Overweight',                                      snomed: '238131007' },
  obesity:      { code: 'E66.9',  display: 'Obesity, unspecified',                            snomed: '414916001' },
  prediabetes:  { code: 'R73.03', display: 'Prediabetes',                                     snomed: '714628002' },
  t2d:          { code: 'E11.9',  display: 'Type 2 diabetes mellitus without complications',  snomed: '44054006' },
  t1d:          { code: 'E10.9',  display: 'Type 1 diabetes mellitus without complications',  snomed: '46635009' },
  ckd:          { code: 'N18.30', display: 'Chronic kidney disease, stage 3 unspecified',     snomed: '709044004' },
  ascvd:        { code: 'I25.10', display: 'Atherosclerotic heart disease of native coronary artery without angina pectoris', snomed: '53741008' },
};

// ─── Canonical patient-facing labels (verbatim from care_pathway_preset_seed.sql
// `conditions`) — the ONE vocabulary written to user_profiles.diagnosed_conditions
// + care_plans.conditions, so both mirror the subtrack and the Elation problem list.
// t1d has no preset; its label follows the t2d form.
const CONDITION_LABEL = {
  htn:          'Hypertension',
  dyslipidemia: 'Dyslipidemia',
  overweight:   'Overweight',
  obesity:      'Obesity',
  prediabetes:  'Pre-diabetes',
  t2d:          'Type 2 diabetes',
  t1d:          'Type 1 diabetes',
  ckd:          'Chronic kidney disease',
  ascvd:        'Atherosclerotic cardiovascular disease',
};

// ─── Condition normalization (funnel slugs + human-readable names) ─
const NORMALIZE = {
  'htn': 'htn', 'hypertension': 'htn', 'high blood pressure': 'htn', 'essential hypertension': 'htn',
  'dyslipidemia': 'dyslipidemia', 'high cholesterol': 'dyslipidemia', 'hyperlipidemia': 'dyslipidemia',
  'high cholesterol / high triglycerides': 'dyslipidemia', 'high triglycerides': 'dyslipidemia',
  'overweight': 'overweight',
  'obesity': 'obesity', 'obese': 'obesity',
  'prediabetes': 'prediabetes', 'pre-diabetes': 'prediabetes', 'pre diabetes': 'prediabetes',
  't2d': 't2d', 'type 2 diabetes': 't2d', 'type-2-diabetes': 't2d', 'diabetes': 't2d', 'type 2 diabetes mellitus': 't2d',
  't1d': 't1d', 'type 1 diabetes': 't1d', 'type-1-diabetes': 't1d', 'type 1 diabetes mellitus': 't1d',
  'ckd': 'ckd', 'chronic kidney disease': 'ckd', 'kidney disease': 'ckd', 'chronic kidney disease (ckd)': 'ckd',
  'cvd': 'ascvd', 'ascvd': 'ascvd', 'heart disease': 'ascvd', 'cardiovascular disease': 'ascvd',
  'heart disease / cardiovascular disease': 'ascvd', 'coronary artery disease': 'ascvd', 'cad': 'ascvd',
  'atherosclerotic cardiovascular disease': 'ascvd',
};

const ECKM_SET = new Set(['htn', 'dyslipidemia', 'overweight', 'obesity', 'prediabetes']);
const CKM_SET = new Set(['t2d', 't1d', 'ckd', 'ascvd']);
// CKM *eligibility* qualifiers — T1D alone is NOT one (ACCESS Participation Agreement:
// the qualifying CKM diabetes dx is Type 2). T1D rides along in the DM1+CKD / DM1+ASCVD
// subtracks but never routes a patient to CKM by itself. So HTN+T1D resolves to eCKM
// (via HTN, with T1D charted as a comorbidity) and T1D-alone stays unresolved (ineligible).
const CKM_QUALIFIERS = new Set(['t2d', 'ckd', 'ascvd']);

// ─── eCKM subtracks (icd10 derived per-condition from CONDITION_ICD10) ─
// Eligible = HTN alone, OR ≥2 of {dyslipidemia, obesity|overweight, prediabetes}.
const ECKM_SUBTRACKS = [
  { subtrack_id: 'ECKM-01', label: 'Dyslipidemia + Overweight',                       tokens: ['dyslipidemia', 'overweight'] },
  { subtrack_id: 'ECKM-02', label: 'Overweight + Prediabetes',                        tokens: ['overweight', 'prediabetes'] },
  { subtrack_id: 'ECKM-03', label: 'Dyslipidemia + Overweight + Prediabetes',         tokens: ['dyslipidemia', 'overweight', 'prediabetes'] },
  { subtrack_id: 'ECKM-04', label: 'HTN + Overweight',                                tokens: ['htn', 'overweight'] },
  { subtrack_id: 'ECKM-05', label: 'HTN + Dyslipidemia + Overweight',                 tokens: ['htn', 'dyslipidemia', 'overweight'] },
  { subtrack_id: 'ECKM-06', label: 'HTN + Overweight + Prediabetes',                  tokens: ['htn', 'overweight', 'prediabetes'] },
  { subtrack_id: 'ECKM-07', label: 'Hypertension (HTN) Only',                         tokens: ['htn'] },
  { subtrack_id: 'ECKM-08', label: 'Dyslipidemia + Obesity',                          tokens: ['dyslipidemia', 'obesity'] },
  { subtrack_id: 'ECKM-09', label: 'Dyslipidemia + Prediabetes',                      tokens: ['dyslipidemia', 'prediabetes'] },
  { subtrack_id: 'ECKM-10', label: 'Obesity + Prediabetes',                           tokens: ['obesity', 'prediabetes'] },
  { subtrack_id: 'ECKM-11', label: 'Dyslipidemia + Obesity + Prediabetes',            tokens: ['dyslipidemia', 'obesity', 'prediabetes'] },
  { subtrack_id: 'ECKM-12', label: 'HTN + Dyslipidemia',                              tokens: ['htn', 'dyslipidemia'] },
  { subtrack_id: 'ECKM-13', label: 'HTN + Obesity',                                   tokens: ['htn', 'obesity'] },
  { subtrack_id: 'ECKM-14', label: 'HTN + Prediabetes',                               tokens: ['htn', 'prediabetes'] },
  { subtrack_id: 'ECKM-15', label: 'HTN + Dyslipidemia + Obesity',                    tokens: ['htn', 'dyslipidemia', 'obesity'] },
  { subtrack_id: 'ECKM-16', label: 'HTN + Dyslipidemia + Prediabetes',               tokens: ['htn', 'dyslipidemia', 'prediabetes'] },
  { subtrack_id: 'ECKM-17', label: 'HTN + Obesity + Prediabetes',                     tokens: ['htn', 'obesity', 'prediabetes'] },
  { subtrack_id: 'ECKM-18', label: 'HTN + Dyslipidemia + Obesity + Prediabetes',      tokens: ['htn', 'dyslipidemia', 'obesity', 'prediabetes'] },
  { subtrack_id: 'ECKM-19', label: 'HTN + Dyslipidemia + Overweight + Prediabetes',   tokens: ['htn', 'dyslipidemia', 'overweight', 'prediabetes'] },
];

// ─── CKM subtracks (explicit icd10 — combination codes per PDF) ───
// Eligible = ≥1 of {DM, CKD 3a/3b, ASCVD}. Subtrack chosen from CKM tokens
// only ({t2d|t1d, ckd, ascvd}); eCKM tokens are superseded for sorting.
const DX = {
  E11_9:  { code: 'E11.9',  display: 'Type 2 diabetes mellitus without complications' },
  E11_22: { code: 'E11.22', display: 'Type 2 diabetes mellitus with diabetic chronic kidney disease' },
  E11_59: { code: 'E11.59', display: 'Type 2 diabetes mellitus with other circulatory complications' },
  E10_22: { code: 'E10.22', display: 'Type 1 diabetes mellitus with diabetic chronic kidney disease' },
  E10_59: { code: 'E10.59', display: 'Type 1 diabetes mellitus with other circulatory complications' },
  N18_30: { code: 'N18.30', display: 'Chronic kidney disease, stage 3 unspecified' },
  I25_10: { code: 'I25.10', display: 'Atherosclerotic heart disease of native coronary artery without angina pectoris' },
};
const CKM_SUBTRACKS = [
  { subtrack_id: 'CKM-01', label: 'Diabetes (DM) Only', tokens: ['t2d'],
    icd10: [{ ...DX.E11_9, conditions: ['t2d'] }] },
  { subtrack_id: 'CKM-02', label: 'CKD Stage 3a/3b Only', tokens: ['ckd'],
    icd10: [{ ...DX.N18_30, conditions: ['ckd'] }] },
  { subtrack_id: 'CKM-03', label: 'ASCVD Only', tokens: ['ascvd'],
    icd10: [{ ...DX.I25_10, conditions: ['ascvd'] }] },
  { subtrack_id: 'CKM-04', label: 'DM1 + CKD', tokens: ['t1d', 'ckd'],
    icd10: [{ ...DX.E10_22, conditions: ['t1d', 'ckd'] }, { ...DX.N18_30, conditions: ['ckd'] }] },
  { subtrack_id: 'CKM-05', label: 'DM2 + CKD', tokens: ['t2d', 'ckd'],
    icd10: [{ ...DX.E11_22, conditions: ['t2d', 'ckd'] }, { ...DX.N18_30, conditions: ['ckd'] }] },
  { subtrack_id: 'CKM-06', label: 'DM1 + ASCVD', tokens: ['t1d', 'ascvd'],
    icd10: [{ ...DX.E10_59, conditions: ['t1d', 'ascvd'] }, { ...DX.I25_10, conditions: ['ascvd'] }] },
  { subtrack_id: 'CKM-07', label: 'DM2 + ASCVD', tokens: ['t2d', 'ascvd'],
    icd10: [{ ...DX.E11_59, conditions: ['t2d', 'ascvd'] }, { ...DX.I25_10, conditions: ['ascvd'] }] },
  { subtrack_id: 'CKM-08', label: 'CKD + ASCVD', tokens: ['ckd', 'ascvd'],
    icd10: [{ ...DX.N18_30, conditions: ['ckd'] }, { ...DX.I25_10, conditions: ['ascvd'] }] },
  { subtrack_id: 'CKM-09', label: 'DM1 + CKD + ASCVD', tokens: ['t1d', 'ckd', 'ascvd'],
    icd10: [{ ...DX.E10_22, conditions: ['t1d', 'ckd'] }, { ...DX.N18_30, conditions: ['ckd'] }, { ...DX.I25_10, conditions: ['ascvd'] }] },
  { subtrack_id: 'CKM-10', label: 'DM2 + CKD + ASCVD', tokens: ['t2d', 'ckd', 'ascvd'],
    icd10: [{ ...DX.E11_22, conditions: ['t2d', 'ckd'] }, { ...DX.N18_30, conditions: ['ckd'] }, { ...DX.I25_10, conditions: ['ascvd'] }] },
];

// Build O(1) lookups keyed by the canonical (sorted) token set.
const ECKM_LOOKUP = buildLookup(ECKM_SUBTRACKS, 'eckm');
const CKM_LOOKUP = buildLookup(CKM_SUBTRACKS, 'ckm');

/**
 * @param {{ conditions: (string[]|string) }} input — patient-reported conditions
 *   (funnel slugs or human-readable names; read raw care_plans.conditions so the
 *   Overweight vs Obesity distinction survives).
 * @returns {{
 *   track: 'eckm'|'ckm', subtrack_id: string|null, mnemonic: string|null,
 *   conditions: string[], status: 'suggested'|'unresolved',
 *   cms_icd10: Array<{code,display,conditions}>,
 *   condition_icd10: Array<{condition,code,display,snomed}>
 * }}
 */
export function resolveSubtrack({ conditions } = {}) {
  const tokens = normalizeConditions(conditions);
  const ckmTokens = tokens.filter((t) => CKM_SET.has(t));
  const eckmTokens = tokens.filter((t) => ECKM_SET.has(t));
  const isCkm = tokens.some((t) => CKM_QUALIFIERS.has(t));

  // Full per-condition list — every reported condition we can code (Elation + chart).
  // Output key is `icd10` (NOT `code`) to match WF12's `_resolveCond` pass-through
  // and the cms_submissions.conditions contract `[{icd10, display, …}]`.
  const condition_icd10 = tokens
    .filter((t) => CONDITION_ICD10[t])
    .map((t) => ({ condition: t, icd10: CONDITION_ICD10[t].code, display: CONDITION_ICD10[t].display, snomed: CONDITION_ICD10[t].snomed }));

  // Subtrack chosen from CKM tokens when present (eCKM superseded for sorting), else eCKM.
  const entry = isCkm ? CKM_LOOKUP.get(keyOf(ckmTokens)) : ECKM_LOOKUP.get(keyOf(eckmTokens));

  if (!entry) {
    return { track: isCkm ? 'ckm' : 'eckm', subtrack_id: null, mnemonic: null, conditions: tokens, status: 'unresolved', cms_icd10: [], condition_icd10 };
  }

  // cms_icd10 = the confirmed subtrack's condition codes ONLY (product rule
  // 2026-07-12: "confirmed subtrack only" — supersedes the 2026-06-19 clinical-v2
  // "nothing dropped"). Comorbidities not part of the subtrack (T1D, HTN on a CKM
  // patient, …) stay in condition_icd10 (Elation/chart), the care plan, and the
  // patient diagnosis — but are NEVER sent to CMS.
  const cms_icd10 = dedupeByCode(subtrackIcd10(entry));

  return { track: entry.track, subtrack_id: entry.subtrack_id, mnemonic: entry.label, conditions: tokens, status: 'suggested', cms_icd10, condition_icd10 };
}

// Map raw funnel/human conditions → canonical main-condition labels for the
// RECOGNISED set only (unrecognised values dropped, same as the subtrack), so the
// two DB columns always equal the subtrack + Elation set. Deterministic + idempotent
// (normalizeConditions sorts + dedupes).
export function resolveConditionLabels(conditions) {
  return normalizeConditions(conditions)
    .filter((t) => CONDITION_LABEL[t])
    .map((t) => CONDITION_LABEL[t]);
}

function buildLookup(subtracks, track) {
  const m = new Map();
  for (const s of subtracks) m.set(keyOf(s.tokens), { ...s, track });
  return m;
}

function normalizeConditions(conditions) {
  const arr = Array.isArray(conditions) ? conditions : conditions != null ? [conditions] : [];
  const seen = new Set();
  for (const raw of arr) {
    if (raw == null) continue;
    const tok = NORMALIZE[String(raw).trim().toLowerCase()];
    if (tok) seen.add(tok);
  }
  // Canonical (sorted) order so the bundle is a pure function of the condition
  // SET — re-running onboarding (or a reordered care_plans.conditions) is idempotent.
  return Array.from(seen).sort();
}

function keyOf(tokens) {
  return Array.from(new Set(tokens)).sort().join('|');
}

function subtrackIcd10(entry) {
  if (entry.icd10) return entry.icd10.map((c) => ({ icd10: c.code, display: c.display, conditions: c.conditions.slice() }));
  return entry.tokens.map((t) => ({ icd10: CONDITION_ICD10[t].code, display: CONDITION_ICD10[t].display, conditions: [t] }));
}

function dedupeByCode(list) {
  const seen = new Set();
  const out = [];
  for (const c of list) { if (seen.has(c.icd10)) continue; seen.add(c.icd10); out.push(c); }
  return out;
}
/* SUBTRACK-CORE-END */

/* ICD-EXT-START */
// ─── CMS-only extended conditions (PM decision 2026-07-11) ────────
// Resolvable for CMS submissions + Align-modal display ONLY. These tokens NEVER
// feed subtrack matching, Elation problems (condition_icd10), or the care-plan
// vocabulary. Displays are the CMS-canonical ICD-10-CM descriptors (formerly
// hand-kept in WF12's inline map).
const EXTENDED_ICD10 = {
  heart_failure:  { code: 'I50.9',  display: 'Heart failure, unspecified' },
  afib:           { code: 'I48.91', display: 'Unspecified atrial fibrillation' },
  sleep_apnea:    { code: 'G47.33', display: 'Obstructive sleep apnea (adult) (pediatric)' },
  esrd:           { code: 'N18.6',  display: 'End stage renal disease' },
  depression:     { code: 'F32.9',  display: 'Major depressive disorder, single episode, unspecified' },
  osteoarthritis: { code: 'M19.90', display: 'Unspecified osteoarthritis, unspecified site' },
  ckd_stage_3a:   { code: 'N18.31', display: 'Chronic kidney disease, stage 3a' },
  ckd_stage_3b:   { code: 'N18.32', display: 'Chronic kidney disease, stage 3b' },
};

// Aliases for the extended set — incl. each ICD display string (lowercased), so
// a stored display round-trips through normalization. No key may collide with
// core NORMALIZE (unit-asserted) — a collision would leak an extended token
// into subtrack matching.
const EXTENDED_NORMALIZE = {
  'heart failure': 'heart_failure', 'congestive heart failure': 'heart_failure', 'chf': 'heart_failure',
  'heart failure, unspecified': 'heart_failure',
  'atrial fibrillation': 'afib', 'afib': 'afib', 'a-fib': 'afib',
  'unspecified atrial fibrillation': 'afib',
  'sleep apnea': 'sleep_apnea', 'obstructive sleep apnea': 'sleep_apnea',
  'obstructive sleep apnea (adult) (pediatric)': 'sleep_apnea',
  'esrd': 'esrd', 'end stage renal disease': 'esrd', 'end-stage renal disease': 'esrd',
  'depression': 'depression',
  'major depressive disorder, single episode, unspecified': 'depression',
  'osteoarthritis': 'osteoarthritis',
  'unspecified osteoarthritis, unspecified site': 'osteoarthritis',
  'ckd_stage_3a': 'ckd_stage_3a', 'ckd stage 3a': 'ckd_stage_3a',
  'chronic kidney disease, stage 3a': 'ckd_stage_3a',
  'ckd_stage_3b': 'ckd_stage_3b', 'ckd stage 3b': 'ckd_stage_3b',
  'chronic kidney disease, stage 3b': 'ckd_stage_3b',
};

// Canonical CORE token for a label/alias/slug, else null (extended set excluded —
// use EXTENDED_NORMALIZE explicitly when the CMS-only vocabulary applies).
export function normalizeCondition(name) {
  return NORMALIZE[String(name ?? '').trim().toLowerCase()] || null;
}

const SUBTRACK_BY_ID = new Map([
  ...ECKM_SUBTRACKS.map((s) => [s.subtrack_id, { ...s, track: 'eckm' }]),
  ...CKM_SUBTRACKS.map((s) => [s.subtrack_id, { ...s, track: 'ckm' }]),
]);

/**
 * Rebuild both ICD-10 views for a condition set, honoring an explicitly-chosen
 * subtrack (care-team override) instead of re-matching. Never absorbs unsupported
 * input — unrecognized entries are reported, never coded.
 *
 * @param {{ conditions: (string[]|string), subtrack_id?: string|null }} input
 * @returns {{
 *   track: 'eckm'|'ckm', subtrack_id: string|null, mnemonic: string|null,
 *   conditions: string[], matches_subtrack: boolean,
 *   cms_icd10: Array<{icd10, display, conditions, extended?}>,
 *   condition_icd10: Array<{condition, label, icd10, display, snomed}>,
 *   unrecognized: string[]
 * }}
 * Invariants: (a) with no subtrack_id and core-only inputs, output ≡ resolveSubtrack
 * (condition_icd10 additionally carries `label`); (b) a subtrack combination row is
 * included only when its backing conditions ⊆ the reported tokens (never assert a
 * combination dx for an unreported condition); (c) cms_icd10 = the confirmed
 * subtrack's codes ONLY (product rule 2026-07-12 "confirmed subtrack only") —
 * comorbidities and extended (CMS-context) conditions are recognized but NOT
 * CMS-coded; extended tokens never enter condition_icd10 either.
 */
export function deriveIcdBundle({ conditions, subtrack_id } = {}) {
  const arr = Array.isArray(conditions) ? conditions : conditions != null ? [conditions] : [];
  const tokens = normalizeConditions(arr);

  // Partition non-core inputs: extended (CMS-only) vs unrecognized.
  const unrecognized = [];
  for (const raw of arr) {
    if (raw == null || String(raw).trim() === '') continue;
    const k = String(raw).trim().toLowerCase();
    if (NORMALIZE[k]) continue;                 // core condition — coded below
    if (EXTENDED_NORMALIZE[k]) continue;        // recognized comorbidity — NOT CMS-coded (subtrack-only rule)
    if (!unrecognized.includes(String(raw).trim())) unrecognized.push(String(raw).trim());
  }

  const condition_icd10 = tokens
    .filter((t) => CONDITION_ICD10[t])
    .map((t) => ({ condition: t, label: CONDITION_LABEL[t], icd10: CONDITION_ICD10[t].code, display: CONDITION_ICD10[t].display, snomed: CONDITION_ICD10[t].snomed }));

  // Explicit subtrack honored when known; otherwise re-match like resolveSubtrack.
  let entry = subtrack_id ? SUBTRACK_BY_ID.get(subtrack_id) : undefined;
  if (!entry) {
    const ckmTokens = tokens.filter((t) => CKM_SET.has(t));
    const eckmTokens = tokens.filter((t) => ECKM_SET.has(t));
    const isCkm = tokens.some((t) => CKM_QUALIFIERS.has(t));
    entry = isCkm ? CKM_LOOKUP.get(keyOf(ckmTokens)) : ECKM_LOOKUP.get(keyOf(eckmTokens));
  }

  if (!entry) {
    return {
      track: tokens.some((t) => CKM_QUALIFIERS.has(t)) ? 'ckm' : 'eckm',
      subtrack_id: null, mnemonic: null, conditions: tokens, matches_subtrack: false,
      cms_icd10: [], condition_icd10, unrecognized,
    };
  }

  // cms_icd10 = the confirmed subtrack's codes ONLY (subtrack-only rule 2026-07-12).
  // Reported comorbidities outside the subtrack + extended CMS-context conditions are
  // recognized but never CMS-coded; they remain in condition_icd10 / the care plan.
  const subtrackRows = subtrackIcd10(entry).filter((c) => c.conditions.every((t) => tokens.includes(t)));

  return {
    track: entry.track,
    subtrack_id: entry.subtrack_id,
    mnemonic: entry.label,
    conditions: tokens,
    matches_subtrack: entry.tokens.every((t) => tokens.includes(t)),
    cms_icd10: dedupeByCode(subtrackRows),
    condition_icd10,
    unrecognized,
  };
}
/* ICD-EXT-END */

// ─── App-only helpers (outside the marker regions — inlined NOWHERE) ─

// Diagnosis-delta preview math for the pathway picker (writes nothing; the clinician
// attests the result via Apply). proposed = subtrack conditions + compatible CORE
// comorbidities (kept); removed = subtrack-conflicting core + non-vocabulary entries
// (always surfaced, never silently lost). Total (null/scalar tolerated; unknown id → null).
// Invariant by construction: resolveSubtrack(proposed).subtrack_id === subtrackId.
export function reconcileConditionsForSubtrack(currentConditions, subtrackId) {
  const entry = SUBTRACK_BY_ID.get(subtrackId);
  if (!entry) return null;

  const arr = Array.isArray(currentConditions) ? currentConditions : currentConditions != null ? [currentConditions] : [];
  const keptTokens = [...entry.tokens];
  const kept = [];
  const removed = [];
  const seen = new Set(entry.tokens);

  for (const raw of arr) {
    if (raw == null || String(raw).trim() === '') continue;
    const tok = NORMALIZE[String(raw).trim().toLowerCase()] || null;
    if (!tok) {
      // Outside the care-plan vocabulary (extended/legacy) — surfaced as a removal.
      const v = String(raw).trim();
      if (!removed.includes(v)) removed.push(v);
      continue;
    }
    if (seen.has(tok)) continue;
    seen.add(tok);
    // Compatible comorbidity iff resolution still lands on the picked subtrack.
    if (resolveSubtrack({ conditions: [...keptTokens, tok] }).subtrack_id === entry.subtrack_id) {
      keptTokens.push(tok);
      kept.push(CONDITION_LABEL[tok]);
    } else if (!removed.includes(CONDITION_LABEL[tok])) {
      removed.push(CONDITION_LABEL[tok]);
    }
  }

  const currentTokens = new Set(normalizeConditions(arr));
  const added = entry.tokens.filter((t) => !currentTokens.has(t)).map((t) => CONDITION_LABEL[t]);
  const proposed = [...entry.tokens.map((t) => CONDITION_LABEL[t]), ...kept];
  return { proposed, added, removed, kept };
}

export { CONDITION_ICD10, CONDITION_LABEL, ECKM_SUBTRACKS, CKM_SUBTRACKS, NORMALIZE, EXTENDED_ICD10, EXTENDED_NORMALIZE };
